// @ts-nocheck

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import Spritesheet from '../../../../components/Spritesheet';
import SoundPlayer from '../../../../singletons/SoundPlayer';
import { SoundIds } from '../../../../types/sounds';
import { KLPoleIslandBackground } from '../../assets/img/backgrounds';
import { KLPole } from '../../assets/img/pole';
import { KLFallingLegsSpritesheet } from '../../assets/img/spritesheets';

import KLPoleTimer from './KLPoleTimer';
import KLSoundButton from './KLSoundButton';
import { KLViewContext } from './KLViewWrapper';

const STAGE_TIME = {
  '1': 30, // min
  '2': 1 * 60, // min
  '3': 2 * 60, // min
  '4': 4 * 60, // min
  '5': 6 * 60, // min
};

const Background = styled.div`
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;

  background: url(${KLPoleIslandBackground});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;

  display: flex;
  flex-direction: column;
  align-items: center;

  filter: brightness(0.4);

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 60%;

    background: linear-gradient(to top, #000000, #000000d0 35%, #00000000);
  }
`;

const PoleImg = styled.img`
  margin-top: -1%;
  margin-right: -5%;
  position: absolute;
  bottom: 0;

  ${({ $height }) => css`
    height: calc(${$height}px + 1%);
  `}
`;

const Wrapper = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding-bottom: 3em;
`;

const Title = styled.div`
  text-transform: uppercase;
  font-weight: bold;
  text-align: center;
  font-size: 2em;
  padding: 0.5em 0 2em 0;

  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${popFadeInAnimation} 1.5s ease;
      animation-fill-mode: forwards;
    `}
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const popFadeInAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const StyledSpritesheet = styled(Spritesheet)`
  position: absolute;
  transform: translate(-35%, 49%) scale(2);
`;

type KLNotificationFallProps = {
  totalTime: number;
};

const KLNotificationFall = ({ totalTime }: KLNotificationFallProps) => {
  const { height: availableHeight } = useContext(KLViewContext);
  const [animateText, setAnimateText] = useState(false);
  const [sound, setSound] = useState(null);
  const loadingSound = useRef(false);

  const onEndAnimation = useCallback(() => {
    setAnimateText(true);
  }, []);

  const isStage = useCallback((stageNumber: string) => totalTime >= STAGE_TIME[stageNumber], [totalTime]);
  const poleFallSound = useMemo(() => {
    if (isStage('5')) {
      return SoundIds.KL_POLE_FALL_STAGE_5;
    } else if (isStage('1')) {
      return SoundIds.KL_POLE_FALL_STAGE_2;
    }
    return SoundIds.KL_POLE_FALL_STAGE_1;
  }, [isStage]);

  useEffect(() => {
    if (poleFallSound && !sound && !loadingSound.current) {
      loadingSound.current = true;
      SoundPlayer.load(poleFallSound).then((s) => {
        setSound(s);
        s.play();
      });
    }

    return () => {
      if (sound) {
        sound.stop();
        setSound(null);
      }
    };
  }, [poleFallSound, sound, setSound]);

  return (
    <>
      {sound ? <KLSoundButton sound={sound} /> : undefined}
      <Wrapper>
        <Content>
          <Title $animate={animateText}>Et c&apos;est la chute !</Title>
        </Content>
        <KLPoleTimer time={totalTime} />
      </Wrapper>
      <Background>
        <StyledSpritesheet
          columnCount={13}
          rowCount={4}
          url={KLFallingLegsSpritesheet}
          keepRatio
          play={1}
          duration={1000}
          height={availableHeight / 2}
          onDone={onEndAnimation}
        />
        <PoleImg src={KLPole} $height={availableHeight / 2} />
      </Background>
    </>
  );
};

export default KLNotificationFall;
